/**
 * ColumnEventListRendererPlugin — `column.event-list` renderer.
 *
 * Default (plain column layout) presentation for event-bearing
 * rows: the column's events as a flat vertical `EntryList` through
 * the shared `ListRenderer`/`ListCell` chrome. Declared by the calendar-list
 * alias template (data-renderer slot `column`), so a calendar-list row on
 * a non-calendar sheet renders its events as a list instead of
 * falling through to the data-json dump.
 *
 * Event reads, calendar-visibility filtering, and the data-model → viewmodel
 * mapping stay on `CalendarListAliasPlugin` (the alias owns the aggregate walk +
 * filter config); this renderer resolves the alias ancestor from the bound
 * aggregate row and delegates both `toProps` and `display`.
 */

import { DateTime, DateUnit } from "@tento-chrono";
import { ComponentIcon, LonaIcons } from "@tento-ui/ui/icons";
import type {
  EntryDisplay,
  ListItem,
} from "@tento-ui/components/entry-list/list-item";
import { type Option } from "@tento-lona/sheets";
import {
  type BindCellContext,
  RowOptionsElement,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow } from "@tento-lona";
import { COLUMN_EVENT_LIST_SPEC, AliasRow } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { CalendarListAliasPlugin } from "../alias/calendar-list.ts";
import { ListRenderer } from "./list-renderer.ts";
import { liveVariantOf } from "../../sheet/live-variant.ts";

/** Nearest ancestor whose rows-side variant is an alias instance —
 *  for the calendar-list template that's the alias row carrying the
 *  filter config the delegate reads. */
function nearestAliasAncestor(
  row: SheetRow.Joined.Handle,
): Option<SheetRow.Joined.Handle> {
  let r: Option<SheetRow.Joined.Handle> = row;
  while (r) {
    if (liveVariantOf(r) instanceof AliasRow) return r;
    r = r.parent();
  }
  return null;
}

/** The renderer plugin that lists a row's events per column. */
export class ColumnEventListRendererPlugin extends ListRenderer {
  static readonly spec: PerTypeSpec = COLUMN_EVENT_LIST_SPEC;

  readonly id: string = "column.event-list";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ComponentIcon);

  constructor(
    makeRowOptions: RowOptionsElement.Factory,
    calendarList: CalendarListAliasPlugin,
  ) {
    super(makeRowOptions, {
      toProps: (context) => eventListItems(calendarList, context),
      display: (context) => {
        const aliasRow = nearestAliasAncestor(context.row) ?? context.row;
        return calendarList.display({ ...context, row: aliasRow });
      },
    });
  }
}

/** The events of the column's range, loaded through the nearest alias's events handle, as list items. */
async function eventListItems(
  calendarList: CalendarListAliasPlugin,
  context: BindCellContext,
): Promise<ListItem[]> {
  const aliasRow = nearestAliasAncestor(context.row) ?? context.row;
  // Nothing else loads event cells in a plain column layout (the
  // calendar layouts run their own per-column loadData), so pull
  // the column's range through the alias's events handle before
  // the delegate reads it. Generous padding amortizes scrolling,
  // mirroring `CalendarListAliasPlugin.makeTimelineProvider`.
  const pd = context.column.discrete()[0];
  const local = context.column.tz;
  const start = local.toWallClock(pd.start.withTime()).toUtc();
  const end = local.toWallClock(pd.end.withTime()).toUtc();
  await aliasRow.events().ensureLoaded(
    new DateTime.Range(start, end),
    DateUnit.days(90),
  );
  return calendarList.toProps({ ...context, row: aliasRow });
}
